import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useNavigate, Link } from "react-router-dom";
import { Toaster } from "sonner";
import Header from "@/components/Header";
import RadarPage from "@/pages/RadarPage";
import WatchlistPage from "@/pages/WatchlistPage";
import AlertsPage from "@/pages/AlertsPage";
import SettingsPage from "@/pages/SettingsPage";
import DesktopPage from "@/pages/DesktopPage";
import AccountsPage from "@/pages/AccountsPage";
import PaymentsPage from "@/pages/PaymentsPage";
import ShippingProfilesPage from "@/pages/ShippingProfilesPage";
import ScalpPage from "@/pages/ScalpPage";
import LandingPage from "@/pages/LandingPage";
import LoginPage from "@/pages/LoginPage";
import RegisterPage from "@/pages/RegisterPage";
import BillingPage from "@/pages/BillingPage";
import ProxiesPage from "@/pages/ProxiesPage";
import AdminPage from "@/pages/AdminPage";
import WaitlistPage from "@/pages/WaitlistPage";
import DesktopRequiredPage from "@/pages/DesktopRequiredPage";
import { RuntimeProvider, useRuntime } from "@/lib/runtime";
import { AuthProvider, useAuth } from "@/lib/auth";
import ProtectedRoute from "@/components/ProtectedRoute";
import VaultGate from "@/components/VaultGate";

function AppShell() {
  const { is_desktop } = useRuntime();
  // 5 routes are desktop-only. On the cloud they render a tutorial page that
  // pitches the .exe install; inside the .exe they render the real feature page.
  const Scalp = is_desktop ? ScalpPage : DesktopRequiredPage;
  const Accounts = is_desktop ? AccountsPage : DesktopRequiredPage;
  const Payments = is_desktop ? PaymentsPage : DesktopRequiredPage;
  const Shipping = is_desktop ? ShippingProfilesPage : DesktopRequiredPage;
  const Proxies = is_desktop ? ProxiesPage : DesktopRequiredPage;
  return (
    <VaultGate>
      <div className="h-screen w-full flex flex-col bg-[#050505] text-white overflow-hidden grain">
        <Header />
        <main className="flex-1 overflow-y-auto" data-testid="main-scroll-container">
          <Routes>
            <Route path="" element={<RadarPage />} />
            <Route path="watchlist" element={<WatchlistPage />} />
            <Route path="alerts" element={<AlertsPage />} />
            <Route path="scalp" element={<Scalp />} />
            <Route path="accounts" element={<Accounts />} />
            <Route path="payments" element={<Payments />} />
            <Route path="shipping" element={<Shipping />} />
            <Route path="proxies" element={<Proxies />} />
            <Route path="settings" element={<SettingsPage />} />
            <Route path="desktop" element={<DesktopPage />} />
            <Route path="*" element={<Navigate to="" replace />} />
          </Routes>
        </main>
      </div>
    </VaultGate>
  );
}

function GatedApp() {
  const { user } = useAuth();
  // Admins bypass everything.
  if (user && user.role !== "admin") {
    // 1) Approval gate — pending/rejected users land on the waitlist screen
    const approval = user.approval_status || "pending";
    if (approval !== "approved") {
      return <Navigate to="/waitlist" replace />;
    }
    // 2) Subscription gate — approved but unpaid → nudge to /billing
    if (user.subscription_status !== "active" && user.subscription_status !== "trialing") {
      return <Navigate to="/billing" replace />;
    }
  }
  return <AppShell />;
}

function BillingGate({ children }) {
  // Pending/rejected users can't see the pay screen — bounce them to /waitlist.
  const { user } = useAuth();
  if (user && user.role !== "admin" && (user.approval_status || "pending") !== "approved") {
    return <Navigate to="/waitlist" replace />;
  }
  return children;
}

function BillingSuccessRedirect() {
  // Stripe redirects to /billing/success?session_id=... — forward to /billing which polls
  const params = new URLSearchParams(window.location.search);
  const sessionId = params.get("session_id") || "";
  return <Navigate to={`/billing?session_id=${sessionId}`} replace />;
}

export default function App() {
  return (
    <AuthProvider>
      <RuntimeProvider>
        <BrowserRouter>
        <Routes>
          <Route path="/" element={<LandingPage />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/register" element={<RegisterPage />} />
          <Route
            path="/billing"
            element={
              <ProtectedRoute>
                <BillingGate>
                  <BillingPage />
                </BillingGate>
              </ProtectedRoute>
            }
          />
          <Route
            path="/waitlist"
            element={
              <ProtectedRoute>
                <WaitlistPage />
              </ProtectedRoute>
            }
          />
          <Route path="/billing/success" element={<BillingSuccessRedirect />} />
          <Route path="/billing/cancel" element={<Navigate to="/billing" replace />} />
          <Route
            path="/admin"
            element={
              <ProtectedRoute>
                <AdminPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/app/*"
            element={
              <ProtectedRoute>
                <GatedApp />
              </ProtectedRoute>
            }
          />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        <Toaster
          theme="dark"
          position="bottom-right"
          toastOptions={{
            style: {
              background: "#0d0d0d",
              border: "1px solid #262626",
              color: "#fff",
              borderRadius: "2px",
              fontFamily: "'IBM Plex Sans', sans-serif",
            },
          }}
        />
        </BrowserRouter>
      </RuntimeProvider>
    </AuthProvider>
  );
}
